import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { PAYMENT_METHODS, enrollmentPaymentSchema } from '@app/shared';
import type {
  CardPaymentInput,
  EnrollmentDto,
  EnrollmentPaymentInput,
  PaymentMethodKey,
} from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { TextInput } from '../../components/form';
import { errorMessage } from '../../lib/api';
import { formatCents } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { CardField } from '../billing/CardField';
import type { CardFieldHandle } from '../billing/CardField';
import { useCompleteEnrollment } from './api';
import { paymentLabel, stepPath } from './labels';
import { CostTable, OrderSummary, WizardGate, costLines } from './wizard';
import type { CostLine } from './wizard';

const TITLE = 'Payment';
/** Release switch of the reference's step 4 layout and Dealer wallet (RELEASE_FEATURES). */
const FEATURE = 'enrollment-payment-step';

type Costs = NonNullable<EnrollmentDto['costs']>;

/**
 * The summary of the reference's step 4, line by line in its order. Processing fee is 0: no
 * category of Payment Processing Fee Config applies to an enrollment, and the card payment that
 * carries one on the reference is not built. Coupon amount is 0: coupons are not taken at
 * enrollment yet.
 */
const referenceLines = (c: Costs, receipt: CostLine['value']): CostLine[] => [
  { label: 'Plan cost (including advance)', value: c.planCents + c.advanceCents },
  { label: 'Activation fee', value: c.activationFeeCents },
  { label: 'Handset cost', value: c.handsetCents },
  { label: 'Plan additional service', value: c.additionalCents },
  { label: 'Regulatory fee', value: c.regulatoryFeeCents },
  { label: 'Plan tax', value: c.taxLines.planCents },
  { label: 'Regulatory fee tax', value: c.taxLines.regulatoryFeeCents },
  { label: 'Activation tax', value: c.taxLines.activationFeeCents },
  { label: 'Handset tax', value: c.taxLines.handsetCents },
  { label: 'Plan additional service tax', value: c.taxLines.additionalCents },
  { label: 'Processing fee', value: 0 },
  { label: 'Receipt number', value: receipt },
  { label: 'Coupon amount', value: 0 },
  { label: 'Total amount', value: c.totalCents, strong: true },
];

const plainLines = (c: Costs): CostLine[] => [
  ...costLines(c, c.advanceMonths === 0 ? 'None' : String(c.advanceMonths)),
  {
    label: 'Tax',
    value: c.taxCents,
    caption:
      'The Tax Rates rate of the service state, when "Calculate tax" is on in System Config.',
  },
  { label: 'Total', value: c.totalCents, strong: true },
];

/** On the reference no method is chosen at first; the choice is checked as a string. */
const referenceSchema = enrollmentPaymentSchema.extend({
  method: z.string().refine((v) => v !== '', 'Select payment method'),
});

const optionLabel = (key: PaymentMethodKey) =>
  PAYMENT_METHODS.find((m) => m.key === key)?.option ?? key;

function MethodChoice({
  control,
  methods,
  reference,
}: {
  control: Control<EnrollmentPaymentInput>;
  methods: PaymentMethodKey[];
  reference: boolean;
}) {
  return (
    <Controller
      control={control}
      name="method"
      render={({ field, fieldState }) => (
        <FormControl error={Boolean(fieldState.error)} required={reference}>
          <FormLabel id="payment-method-label">Payment method</FormLabel>
          <RadioGroup
            row
            aria-labelledby="payment-method-label"
            name={field.name}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
          >
            {PAYMENT_METHODS.filter((m) => methods.includes(m.key)).map((m) => (
              <FormControlLabel
                key={m.key}
                value={m.key}
                label={reference ? optionLabel(m.key) : paymentLabel(m.key)}
                control={<Radio size="small" />}
              />
            ))}
          </RadioGroup>
          {fieldState.error ? <FormHelperText>{fieldState.error.message}</FormHelperText> : null}
        </FormControl>
      )}
    />
  );
}

/*
 * Step 4. With its release switch on, it follows the reference's step 4 (docs/telgoo5-reference/
 * modules/04-enrollment.md): the summary with tax per item, Receipt number and Coupon amount, the
 * methods Agent cash (PC318), Skip payment (PC320) and Dealer wallet (PC544), none chosen at
 * first, and "Review and proceed". Credit Card (PC319) is not offered: no card data is collected
 * until a payment gateway supplies tokens. What "Review and proceed" shows was not seen; here it
 * opens a review of the payment, then the line is activated at the carrier.
 * With the switch off it is this project's earlier layout: Cash and Skip Payment, Complete Enrollment.
 */
function PaymentForm({ enrollment, reference }: { enrollment: EnrollmentDto; reference: boolean }) {
  const navigate = useNavigate();
  const complete = useCompleteEnrollment(enrollment.id);
  const [failure, setFailure] = useState<string | null>(null);
  const [review, setReview] = useState<EnrollmentPaymentInput | null>(null);
  const methods = reference
    ? enrollment.paymentMethods
    : enrollment.paymentMethods.filter((m) => m !== 'dealerWallet');
  const costs = enrollment.costs;
  const handover = enrollment.handover;
  const fraud = enrollment.review;
  // An order held by fraud prevention is paid only after someone approves it (Review menu).
  const held = fraud?.status === 'Pending Audit' || fraud?.status === 'Rejected';
  const wallet = enrollment.dealerWalletCents ?? 0;
  const cardRef = useRef<CardFieldHandle>(null);

  const { control, handleSubmit, setError } = useForm<EnrollmentPaymentInput>({
    resolver: zodResolver(reference ? referenceSchema : enrollmentPaymentSchema),
    defaultValues: {
      method: reference ? ('' as PaymentMethodKey) : methods[0],
      receiptNumber: '',
      portPin: '',
    },
  });
  const portIn = handover?.type === 'port-in' ? handover.portIn : null;
  const method = useWatch({ control, name: 'method' });
  const walletShort = method === 'dealerWallet' && costs ? costs.totalCents > wallet : false;

  const pay = async (values: EnrollmentPaymentInput) => {
    setFailure(null);
    let card: CardPaymentInput | undefined;
    if (values.method === 'card') {
      try {
        card = await cardRef.current?.collect();
      } catch (err) {
        setReview(null);
        return setFailure((err as Error).message);
      }
    }
    try {
      const done = await complete.mutateAsync({ ...values, card });
      navigate(stepPath(done.id, 'done'));
    } catch (err) {
      // A carrier refusal (502) leaves the enrollment at this step: say why and let the user
      // try again. Reading the enrollment again also covers the other outcomes: a SIM that was
      // used meanwhile sends the user back, an order completed elsewhere goes to its confirmation.
      setReview(null);
      setFailure(errorMessage(err));
      void complete.refresh();
    }
  };

  const submit = handleSubmit(async (values) => {
    // The old account's PIN goes with the port request and is never stored.
    if (portIn && !values.portPin?.trim())
      return setError('portPin', { message: 'Port PIN/Password is required' });
    if (!reference) return pay(values);
    if (values.method === 'dealerWallet' && walletShort)
      return setError('method', {
        message: `Your dealer wallet holds ${formatCents(wallet)}; the order costs ${formatCents(costs?.totalCents ?? 0)}.`,
      });
    setFailure(null);
    setReview(values);
  });

  const receipt = (
    <TextInput
      control={control}
      name="receiptNumber"
      label="Receipt number"
      size="small"
      autoComplete="off"
      disabled={method !== 'cash'}
      slotProps={{ htmlInput: { maxLength: 40 } }}
    />
  );
  const lines: CostLine[] = costs
    ? reference
      ? referenceLines(costs, receipt)
      : plainLines(costs)
    : [];
  const summaryLabel = reference ? 'Payment summary' : 'Overall Cost Details';

  return (
    <Box>
      <PageHeader title={TITLE} code="PC1142" />
      <Paper
        variant="outlined"
        component="form"
        noValidate
        onSubmit={(e) => void submit(e)}
        aria-label={TITLE}
        sx={{ p: 2, maxWidth: 1040 }}
      >
        <OrderSummary enrollment={enrollment} />
        <Box
          component="dl"
          aria-label="Device"
          sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 0.5, m: 0, mb: 2 }}
        >
          {(handover?.type === 'shipment'
            ? ([
                ['Handover', 'Shipment'],
                ['Model', handover.model?.name ?? ''],
                ['Carrier', `${handover.carrier.name} (${handover.carrier.code})`],
              ] as const)
            : ([
                ['SIM#', handover?.sim ?? ''],
                ['MDN#', handover?.mdn ?? 'Assigned on activation'],
                ['Carrier', handover ? `${handover.carrier.name} (${handover.carrier.code})` : ''],
              ] as const)
          ).map(([label, value]) => (
            <Box key={label} sx={{ display: 'flex', gap: 0.75 }}>
              <Typography component="dt" sx={{ fontWeight: 600 }}>
                {label}:
              </Typography>
              <Typography component="dd" sx={{ m: 0 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {held ? (
          <Alert severity={fraud?.status === 'Rejected' ? 'error' : 'warning'} sx={{ mb: 2 }}>
            {fraud?.status === 'Rejected'
              ? `This order was rejected in fraud review: ${fraud.reason}`
              : `This order is waiting in the fraud review queue and cannot be completed until it is approved. ${fraud?.reason ?? ''}`}
          </Alert>
        ) : null}
        {failure ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {failure}
          </Alert>
        ) : enrollment.incompleteReason && !held ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The last attempt did not go through: {enrollment.incompleteReason}
          </Alert>
        ) : null}

        <Typography variant="h3" sx={{ mb: 1 }}>
          {summaryLabel}
        </Typography>
        <CostTable label={summaryLabel} lines={lines} />

        <Box sx={{ mt: 3, mb: 2 }}>
          {methods.length === 0 ? (
            <Alert severity="warning">
              {reference
                ? 'No payment method is available to you for this enrollment. Agent cash and Skip payment are switched on in System Config and need permission PC318 or PC320; Dealer wallet needs PC544.'
                : 'No payment method is available to you for this enrollment. Cash and Skip Payment are switched on in System Config and need permission PC318 or PC320.'}
            </Alert>
          ) : (
            <>
              <MethodChoice control={control} methods={methods} reference={reference} />
              {!reference && method === 'cash' ? (
                <Box sx={{ maxWidth: 320, mt: 1.5 }}>
                  <TextInput
                    control={control}
                    name="receiptNumber"
                    label="Receipt Number"
                    autoComplete="off"
                    slotProps={{ htmlInput: { maxLength: 30 } }}
                  />
                </Box>
              ) : null}
              {method === 'card' ? (
                <Box sx={{ maxWidth: 480, mt: 1.5 }}>
                  <CardField ref={cardRef} />
                </Box>
              ) : null}
              {reference && method === 'dealerWallet' ? (
                <Box aria-label="Dealer wallet" role="group" sx={{ mt: 1.5 }}>
                  <Typography>Dealer wallet balance: {formatCents(wallet)}</Typography>
                  {walletShort ? (
                    <Typography color="error" sx={{ fontSize: 13 }}>
                      The wallet does not hold the total amount. Add funds in Customer Service &gt;
                      Dealer Wallet.
                    </Typography>
                  ) : (
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      The total amount is taken from your dealer wallet.
                    </Typography>
                  )}
                </Box>
              ) : null}
              {portIn ? (
                <Box sx={{ maxWidth: 320, mt: 1.5 }}>
                  <TextInput
                    control={control}
                    name="portPin"
                    label="Port PIN/Password"
                    required
                    type="password"
                    autoComplete="off"
                    helperText={`For porting ${portIn.mdn}; sent to the carrier, never stored.`}
                    slotProps={{ htmlInput: { maxLength: 20 } }}
                  />
                </Box>
              ) : null}
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={complete.isPending || methods.length === 0 || held}
            startIcon={
              complete.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
            }
          >
            {reference ? 'Review and proceed' : 'Complete Enrollment'}
          </Button>
          <Button component={Link} to={stepPath(enrollment.id, 3)} disabled={complete.isPending}>
            Back
          </Button>
        </Box>
      </Paper>

      {review && costs ? (
        <Dialog open onClose={() => !complete.isPending && setReview(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Review and proceed</DialogTitle>
          <DialogContent dividers>
            <Box component="dl" aria-label="Payment review" sx={{ m: 0 }}>
              {(
                [
                  [
                    'Customer',
                    `${enrollment.applicant?.firstName ?? ''} ${enrollment.applicant?.lastName ?? ''}`,
                  ],
                  ['Plan', enrollment.plan?.name ?? ''],
                  ['Payment method', optionLabel(review.method)],
                  ...(review.method === 'cash'
                    ? [['Receipt number', review.receiptNumber || '-'] as const]
                    : []),
                  ...(review.method === 'dealerWallet'
                    ? [['Wallet balance after', formatCents(wallet - costs.totalCents)] as const]
                    : []),
                  [
                    'Total amount',
                    review.method === 'skip'
                      ? `${formatCents(costs.totalCents)} (not collected)`
                      : formatCents(costs.totalCents),
                  ],
                ] as const
              ).map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                  <Typography component="dt" sx={{ fontWeight: 600 }}>
                    {label}:
                  </Typography>
                  <Typography component="dd" sx={{ m: 0 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography color="text.secondary" sx={{ fontSize: 13, mt: 1 }}>
              {handover?.type === 'shipment'
                ? 'The order then goes to the shipping queue.'
                : 'The line is then activated at the carrier.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReview(null)} disabled={complete.isPending}>
              Back
            </Button>
            <Button
              variant="contained"
              disabled={complete.isPending}
              startIcon={
                complete.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
              }
              onClick={() => void pay(review)}
            >
              Proceed
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Box>
  );
}

function Payment({ enrollment }: { enrollment: EnrollmentDto }) {
  const featureOn = useFeatureReleased();
  const reference = featureOn(FEATURE);
  // The form starts again when the switch is read, so its first choice fits the layout.
  return <PaymentForm key={String(reference)} enrollment={enrollment} reference={reference} />;
}

/** Step 4 of the wizard (PC1142): payment, then activation of the SIM at the carrier. */
export function PaymentPage() {
  return (
    <WizardGate step={4} title={TITLE}>
      {(enrollment) => <Payment key={enrollment.id} enrollment={enrollment} />}
    </WizardGate>
  );
}
